import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.modules.scss';
import QOStyles from '../styles.module.scss';
import { useTranslation } from 'react-i18next';
import AlternativePartsTable from './AlternativePartsTable/AlternativePartsTable';
import {
  AlloyButton,
  ComponentLoader,
  StickySection
} from 'cat-ecommerce-alloy';
import {
  ADD_TO_CART_MODIFICATIONS_RESET,
  ADD_TO_CART_SET_PHASE,
  GET_DEALER_PRICE_AND_AVAILABILITY_RESET,
  RESET_CURRENT_MODAL_PHASE,
  SET_CURRENT_MODAL_PHASE,
  UPDATE_PARTS
} from '@app/store/checkout/constants';
import {
  ADD_TO_CART_PHASES,
  ALTERNATIVE
} from '../../ShoppingCartPage/constants';
import { PAGE_BREAKPOINTS, STATUS } from '@app/constants/commonConstants';
import { useBreakpoint } from '@app/hooks';
import { getItemsToSend, getOriginalItemsToSend, getQuantities } from './utils';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import { clearError, setError } from '@app/store/exception/actions';
import { getAltParts } from '@app/store/checkout/utils';
import ExceptionFeedbackModal from '../common/ExceptionFeedbackModal/ExceptionFeedbackModal';
import { useValidateQuantity } from '../hooks';
import Disclaimers from '@app/components/pages/checkout/shopping-cart/QuickOrder/common/Disclaimers';
import PreferenceToggle from '../common/PreferenceToggle';

const AlternativePartsModal = ({
  modalOptions,
  isChildModal = false,
  isAvailabilityToggled,
  setIsAvailabilityToggled
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dispatch = useDispatch();
  const showBack = !isMD || isChildModal;
  const [quantityError, setQuantityError] = useState(false);
  const { validateAllItemQuantities, validateItemQuantity } =
    useValidateQuantity();

  const addToCartStatus = useSelector(s => s.checkout.addToCartStatus);
  const isAddToCartStatusLoading = addToCartStatus === STATUS.PENDING;
  const errorSelector = state =>
    state.errors[ERROR_DOMAIN.QUICK_ORDER]?.[ERROR_PATH.ADD_TO_CART];
  const currency = useSelector(
    s => s.checkout.currentAddToCartModifications.currency
  );
  const error = useSelector(errorSelector);
  const items = useSelector(
    s => s.checkout.currentAddToCartModifications.items
  );

  const [currentItems] = useState(items);

  const [quantities, setQuantities] = useState(getQuantities(items));

  const onBackCancel = cancelOption => {
    if (!isChildModal || cancelOption) {
      dispatch({ type: GET_DEALER_PRICE_AND_AVAILABILITY_RESET });
      dispatch({ type: ADD_TO_CART_MODIFICATIONS_RESET });
    }
    dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
    dispatch({ type: RESET_CURRENT_MODAL_PHASE });
    modalOptions.close();
  };

  const showQuantityError = (item, itemQuantity) => {
    if (!quantityError) {
      return null;
    }
    const itemsToSend = getItemsToSend(quantities, currentItems);
    const hasQuantityErrors = validateAllItemQuantities(itemsToSend);
    if (!hasQuantityErrors) {
      dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
      setQuantityError(false);
    }

    return validateItemQuantity(item, itemQuantity)
      ? t('SKULIST_INVALID_QUANTITY')
      : null;
  };

  const validateQuantitiesAndDispatch = (addOnlyOriginalParts = false) => {
    const itemsToSend = addOnlyOriginalParts
      ? getOriginalItemsToSend(quantities, currentItems)
      : getItemsToSend(quantities, currentItems);
    const hasItemWithQuantity = Object.keys(itemsToSend).some(
      key => !!itemsToSend[key].requestedQuantity
    );
    if (hasItemWithQuantity) {
      const hasQuantityError = validateAllItemQuantities(itemsToSend);
      if (hasQuantityError) {
        setQuantityError(true);
        dispatch(
          setError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART, {
            title: 'UNKNOWN_ERROR_TITLE',
            message: 'QO_MAXIMUM_QUANTITY_UPDATED_ERROR'
          })
        );
      } else {
        setQuantityError(false);
        dispatch({ type: UPDATE_PARTS, payload: itemsToSend });
        dispatch({
          type: ADD_TO_CART_SET_PHASE,
          payload: ADD_TO_CART_PHASES.ADD_PARTS_TO_CART
        });
      }
    } else if (addOnlyOriginalParts) {
      setQuantityError(false);
      dispatch({ type: UPDATE_PARTS, payload: itemsToSend });
      dispatch({
        type: ADD_TO_CART_SET_PHASE,
        payload: ADD_TO_CART_PHASES.ADD_PARTS_TO_CART
      });
    }
    dispatch({ type: RESET_CURRENT_MODAL_PHASE });
  };

  const onUpdate = () => {
    validateQuantitiesAndDispatch(false);
  };

  const onSkipThisStep = () => {
    validateQuantitiesAndDispatch(true);
  };

  useEffect(() => {
    dispatch({ type: SET_CURRENT_MODAL_PHASE, payload: ALTERNATIVE });
  }, []);

  return (
    <>
      {isAddToCartStatusLoading && (
        <ComponentLoader contentClassName="position-sticky" />
      )}
      <ExceptionFeedbackModal
        errorSelector={errorSelector}
        tryAgainButtonProps={{
          onClick: onUpdate,
          className: quantityError && 'd-none'
        }}
        onCancelImportButton={() => onBackCancel(showBack)}
        cancelImportButtonClassName={quantityError && 'ps-0'}
      />
      <div className={cx('pb-3 u-fs--16', styles['subtitle-text'])}>
        {t('ALTERNATE_PARTS_MODAL_SUBTITLE')}
      </div>
      <PreferenceToggle
        id="AltModal"
        isAvailabilityToggled={isAvailabilityToggled}
        setIsAvailabilityToggled={setIsAvailabilityToggled}
      />
      <div className={cx('pt-3 pb-3')}>
        {Object.keys(currentItems).map(
          (key, sqIndex) =>
            !!getAltParts(currentItems[key]).length && (
              <div
                className={cx('mb-3', styles['table-border'])}
                key={currentItems[key].referenceId}
              >
                <AlternativePartsTable
                  sequence={`${sqIndex + 1})`}
                  item={currentItems[key]}
                  currency={currency}
                  alternateParts={getAltParts(currentItems[key])}
                  quantities={quantities[key]}
                  setQuantities={setQuantities}
                  showQuantityError={showQuantityError}
                  isAvailabilityToggled={isAvailabilityToggled}
                />
              </div>
            )
        )}
      </div>
      <Disclaimers />
      <StickySection
        className={cx(
          QOStyles['sticky-section'],
          'd-flex mt-3 ps-2 pe-4 py-3 bg-white z-index-1 shadow rounded',
          `${isMD ? 'flex-row justify-content-between' : 'flex-column'}`
        )}
      >
        <div
          className={`d-flex  pe-2 ${
            isMD
              ? 'justify-content-around order-2'
              : 'justify-content-center order-1'
          }`}
        >
          <AlloyButton
            buttonType="ghost"
            onClick={onSkipThisStep}
            disabled={!!quantityError}
            className={`${isMD ? 'me-3' : 'me-2'}`}
          >
            {t('SKIP_THIS_STEP')}
          </AlloyButton>
          <AlloyButton
            buttonType="primary"
            onClick={onUpdate}
            disabled={!!error || !!quantityError}
          >
            {t('CONTINUE')}
          </AlloyButton>
        </div>
        <div
          className={`d-flex justify-content-center ${
            isMD ? 'order-1' : 'order-2'
          }`}
        >
          <AlloyButton
            buttonType="ghost"
            onClick={() => onBackCancel(!showBack)}
          >
            {showBack ? t('BACK') : t('CANCEL')}
          </AlloyButton>
        </div>
      </StickySection>
    </>
  );
};

AlternativePartsModal.propTypes = {
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  close: PropTypes.func,
  isChildModal: PropTypes.bool,
  modalOptions: PropTypes.shape({
    isChild: PropTypes.bool,
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  })
};

export default AlternativePartsModal;
